import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useEmailAccounts, useEmailAccountSendIdentities } from './useData';
import { moduleConfig } from './config';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import { Button, PlusIcon } from '../../components/ui';
import { useNotification } from '../../contexts/NotificationContext';
import { useModuleConfigStrings } from '../../i18n/useModuleConfigStrings';
import EmailAccountCard from './components/EmailAccountCard';
import EmailAccountsListHeader from './components/EmailAccountsListHeader';

const List: React.FC = () => {
  const { colors } = useTheme();
  const { showError } = useNotification();
  const strings = useModuleConfigStrings(moduleConfig.module.i18nKey, moduleConfig);
  const navigate = useNavigate();
  const { data: accounts, isLoading: loading, mutate } = useEmailAccounts();
  const { byAccount: identitiesByAccount } = useEmailAccountSendIdentities();
  const [deletingId, setDeletingId] = React.useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = React.useState<number | null>(null);

  const summary = useMemo(() => {
    let syncingCount = 0;
    let errorCount = 0;
    let pausedCount = 0;
    for (const account of accounts) {
      if (!account.syncEnabled) pausedCount += 1;
      else if (account.syncStatus === 'syncing') syncingCount += 1;
      else if (account.syncStatus === 'error') errorCount += 1;
    }
    return { syncingCount, errorCount, pausedCount };
  }, [accounts]);

  const handleDeleteConfirm = async () => {
    const id = deleteConfirmId;
    if (id == null) return;
    setDeleteConfirmId(null);
    setDeletingId(id);
    try {
      await moduleConfig.delete.handler(id);
      await mutate();
    } catch {
      showError(strings.deleteErrorMessage);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className={cn('flex flex-col gap-4 p-4 md:p-6', colors.background)}>
        <div className={cn('h-28 animate-pulse rounded-xl border', colors.border, colors.surfaceHover)} />
        <div className="space-y-4">
          {[1, 2].map((key) => (
            <div key={key} className={cn('h-52 animate-pulse rounded-xl border', colors.border, colors.surfaceHover)} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-4 p-4 md:p-6', colors.background)}>
      <EmailAccountsListHeader
        totalAccounts={accounts.length}
        syncingCount={summary.syncingCount}
        errorCount={summary.errorCount}
        pausedCount={summary.pausedCount}
        onAdd={() => navigate(moduleConfig.module.route.new)}
        addLabel="Add Email Account"
      />

      {accounts.length === 0 ? (
        <div
          className={cn(
            'flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-16 text-center',
            colors.border,
            colors.surface,
          )}
        >
          <div
            className={cn(
              'mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border',
              colors.border,
              colors.surfaceHover,
              'text-indigo-600 dark:text-indigo-400',
            )}
          >
            <Mail className="h-7 w-7" aria-hidden />
          </div>
          <p className={cn('text-base font-medium', colors.text)}>{strings.emptyNoData}</p>
          <p className={cn('mt-1 max-w-md text-sm', colors.textMuted)}>
            Connect an IMAP, POP3, or Google mailbox to sync mail and configure send-as identities.
          </p>
          <Button type="button" className="mt-6" onClick={() => navigate(moduleConfig.module.route.new)}>
            <PlusIcon className="h-5 w-5" />
            Add Email Account
          </Button>
        </div>
      ) : (
        <ul className="grid gap-4">
          {accounts.map((account) => (
            <li key={account.id}>
              <EmailAccountCard
                account={account}
                identities={identitiesByAccount[account.id]?.identities ?? []}
                detailHref={moduleConfig.module.route.detail(account.id)}
                editHref={moduleConfig.module.route.edit(account.id)}
                onDelete={() => setDeleteConfirmId(account.id)}
                deleting={deletingId === account.id}
                onShared={() => void mutate()}
              />
            </li>
          ))}
        </ul>
      )}

      <ConfirmationDialog
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={handleDeleteConfirm}
        title={strings.deleteTitle}
        message={strings.deleteMessage}
        confirmText={strings.deleteConfirmText}
        cancelText={strings.deleteCancelText}
        confirmVariant="danger"
      />
    </div>
  );
};

export default List;
